import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQuery} from '@github-ui/react-query'
import {Link, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useSearchParams} from '@github-ui/use-navigate'
import {AlertIcon, BookIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import {savedViewsQueryOptions} from '../../queries/saved-views'
import styles from './RepositoryViews.module.css'
import {DEFAULT_VIEWS_SORT, isViewsSortValue, sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'

export function ViewsIndexContent() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const sortParam = searchParams.get('sort')
  const sort = isViewsSortValue(sortParam) ? sortParam : DEFAULT_VIEWS_SORT
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (next === DEFAULT_VIEWS_SORT) {
            params.delete('sort')
          } else {
            params.set('sort', next)
          }
          return params
        },
        {replace: true, preventAutofocus: true},
      )
    },
    [setSearchParams],
  )

  const newViewUrl = `/${owner}/${repo}/issues/views/new`

  const {trackIndexVisited} = useSavedViewAnalytics()
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackIndexVisited()
  }, [trackIndexVisited])

  const {data, isError, isFetching, refetch} = useQuery(savedViewsQueryOptions({owner, repo}))
  const sortedViews = useMemo(() => sortViews(data?.savedViews ?? [], sort), [data?.savedViews, sort])
  const isLoading = isFetching && !data && !isError
  const showError = isError && !data
  const hasViews = !!data && sortedViews.length > 0

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Something went wrong</Blankslate.Heading>
      <Blankslate.Description>We couldn’t load your views. Please try again.</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>Try again</Blankslate.PrimaryAction>
    </Blankslate>
  )

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Something went wrong</Blankslate.Heading>
      <Blankslate.Description>We couldn’t display your views. Try reloading the page.</Blankslate.Description>
    </Blankslate>
  )

  const emptyBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>No views yet</Blankslate.Heading>
      <Blankslate.Description>
        Save a search as a view. Views are visible to everyone with repository access.
      </Blankslate.Description>
      <Blankslate.PrimaryAction href={newViewUrl}>New view</Blankslate.PrimaryAction>
    </Blankslate>
  )

  const listProps = {views: sortedViews, owner, repo, sort, onSortChange: handleSortChange}

  return (
    <div className={styles.viewsPage}>
      <div className={styles.headerRow}>
        {sidebarLayoutEnabled ? null : (
          <Heading as="h1" className={styles.heading}>
            Views
          </Heading>
        )}
        {hasViews && (
          <Button as={Link} to={newViewUrl} variant="primary">
            New view
          </Button>
        )}
      </div>
      <div className={styles.viewsListWrapper} data-hpc containertiming="hpc" aria-busy={isLoading}>
        <ErrorBoundary boundaryName="RepositoryViewsIndexPage" fallback={renderErrorFallback}>
          {isLoading ? (
            <ViewsList {...listProps} isLoading />
          ) : showError ? (
            errorBlankslate
          ) : (
            <ViewsList {...listProps} emptyState={emptyBlankslate} />
          )}
        </ErrorBoundary>
      </div>
    </div>
  )
}
